import { type ComboboxItem } from '@consigli/facade';
import { type OracleProject, type UnchargeableReason } from '@consigli/types';
import { type TFunction } from 'i18next';

/**
 * Why a project cannot be charged to, as a message key.
 *
 * Shared by the project creation form and the project Settings tab, which offer
 * the same picker against the same API and must explain a greyed-out row the
 * same way.
 */
export const UNCHARGEABLE_MESSAGE_KEY: Record<UnchargeableReason, string> = {
  project_status_does_not_allow_charges: 'project-creation.oracle-unchargeable-project-status',
  project_dates_do_not_allow_charges: 'project-creation.oracle-unchargeable-project-dates',
  task_is_not_chargeable: 'project-creation.oracle-unchargeable-task-not-chargeable',
  task_passed_active_end_date: 'project-creation.oracle-unchargeable-task-end-date',
  transaction_control_does_not_allow_charges:
    'project-creation.oracle-unchargeable-transaction-controls',
};

/**
 * Oracle search results as Combobox rows.
 *
 * The number goes in `sublabel` and the name in `label`, which is what renders
 * the "03001027 — AECOM Controls" the design shows: the Combobox draws the
 * sublabel first, in medium, then an em dash, then the label.
 *
 * An unchargeable project stays in the list and is disabled rather than
 * filtered out — somebody searching for it by name needs to find out *why* they
 * cannot pick it, and an absent row says nothing.
 */
export const toOracleComboboxItems = (
  projects: readonly OracleProject[],
  t: TFunction,
): ComboboxItem[] =>
  projects.map((project) => ({
    key: project.projectNumber,
    label: project.projectName,
    sublabel: project.projectNumber,
    disabled: !project.chargeable,
    disabledTitle: project.unchargeableReason
      ? t(UNCHARGEABLE_MESSAGE_KEY[project.unchargeableReason])
      : undefined,
  }));
